import type { TFunction } from "i18next";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { matchPath, useLocation } from "react-router-dom";

type RouteTitle = {
  patterns: string[];
  title: (t: TFunction) => string;
};

// Ordered most-specific first: the first matching pattern wins.
const ROUTE_TITLES: RouteTitle[] = [
  { patterns: ["/"], title: (t) => t("Home") },
  { patterns: ["/landing"], title: (t) => t("Home") },
  { patterns: ["/login", "/sso"], title: (t) => t("Log in") },
  { patterns: ["/tasks/*"], title: (t) => t("Tasks") },
  { patterns: ["/order/*"], title: (t) => t("New order") },
  { patterns: ["/groups/create"], title: (t) => t("Create group") },
  { patterns: ["/groups"], title: (t) => t("Groups") },
  { patterns: ["/groups/:groupId/*"], title: (t) => t("Group details") },
  {
    patterns: ["/cases/*", "/group-overview/*"],
    title: (t) => t("Cases"),
  },
  { patterns: ["/monitoring/*"], title: (t) => t("Monitoring") },
  {
    patterns: ["/case-details/:caseId/*", "/legacy-case-details/:reportId/*"],
    title: (t) => t("Case details"),
  },
  { patterns: ["/quickscreen/:caseId/*"], title: (t) => t("Quickscreen") },
  { patterns: ["/profiles"], title: (t) => t("Profiles") },
  { patterns: ["/profiles/:subjectId"], title: (t) => t("Profile") },
  { patterns: ["/packages/create"], title: (t) => t("Create package") },
  { patterns: ["/packages"], title: (t) => t("Packages") },
  { patterns: ["/package/:packageId/*"], title: (t) => t("Package details") },
  { patterns: ["/apply-link/create"], title: (t) => t("Create apply link") },
  { patterns: ["/apply-links"], title: (t) => t("Apply links") },
  {
    patterns: ["/apply-link/:applyLinkId/*"],
    title: (t) => t("Apply link details"),
  },
  {
    patterns: ["/adjudication/create"],
    title: (t) => t("Create adjudication rubric"),
  },
  { patterns: ["/adjudication"], title: (t) => t("Adjudication") },
  {
    patterns: ["/adjudication/:adjudicationId/*"],
    title: (t) => t("Adjudication rubric"),
  },
  {
    patterns: ["/billing/invoices/*", "/pay-invoice/:invoiceId"],
    title: (t) => t("Invoices"),
  },
  {
    patterns: ["/billing/billing-profiles/*"],
    title: (t) => t("Billing profiles"),
  },
  {
    patterns: ["/billing/payment-profiles/*"],
    title: (t) => t("Payment profiles"),
  },
  { patterns: ["/billing/*"], title: (t) => t("Billing") },
  {
    patterns: ["/settings/users", "/settings/user/*"],
    title: (t) => t("Users"),
  },
  { patterns: ["/settings/tags"], title: (t) => t("Tags") },
  { patterns: ["/settings/branding"], title: (t) => t("Branding") },
  {
    patterns: ["/settings/integrations/*", "/settings/ats-integrations"],
    title: (t) => t("Integrations"),
  },
  { patterns: ["/settings/security"], title: (t) => t("Security") },
  { patterns: ["/settings/check-settings"], title: (t) => t("Checks") },
  {
    patterns: ["/settings/bankruptcy-templates/*"],
    title: (t) => t("Bankruptcy templates"),
  },
  {
    patterns: ["/settings/civil-litigation-templates/*"],
    title: (t) => t("Civil litigation templates"),
  },
  {
    patterns: ["/settings/credential-verification-templates/*"],
    title: (t) => t("Credential verification templates"),
  },
  {
    patterns: ["/settings/credit-report-templates/*"],
    title: (t) => t("Credit report templates"),
  },
  {
    patterns: ["/settings/criminal-record-templates/*"],
    title: (t) => t("Criminal record templates"),
  },
  {
    patterns: ["/settings/custom-questionnaire-templates/*"],
    title: (t) => t("Custom questionnaire templates"),
  },
  {
    patterns: ["/settings/directorship-templates/*"],
    title: (t) => t("Directorship templates"),
  },
  {
    patterns: ["/settings/education-verification-templates/*"],
    title: (t) => t("Education verification templates"),
  },
  {
    patterns: ["/settings/employment-verification-templates/*"],
    title: (t) => t("Employment verification templates"),
  },
  {
    patterns: ["/settings/experience-verification-templates/*"],
    title: (t) => t("Experience verification templates"),
  },
  {
    patterns: ["/settings/fitness-propriety-templates/*"],
    title: (t) => t("Fitness & propriety templates"),
  },
  {
    patterns: ["/settings/international-criminal-database-templates/*"],
    title: (t) => t("International criminal database templates"),
  },
  {
    patterns: ["/settings/questionnaire-templates/*"],
    title: (t) => t("Questionnaire templates"),
  },
  {
    patterns: ["/settings/reference-check-templates/*"],
    title: (t) => t("Reference templates"),
  },
  { patterns: ["/settings/*"], title: (t) => t("Settings") },
];

export function getRoutePageTitle(pathname: string, t: TFunction) {
  const match = ROUTE_TITLES.find(({ patterns }) =>
    patterns.some((pattern) => matchPath(pattern, pathname))
  );
  return match ? match.title(t) : null;
}

export function useDocumentTitle() {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const page = getRoutePageTitle(pathname, t);
    document.title = page
      ? t("{{page}} — Client Portal", { page })
      : t("Client Portal");
  }, [pathname, t, i18n.language]);
}
